import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../ui/dialog";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "../../ui/collapsible";
import { Globe, CheckCircle, XCircle, Trash2, Plus, Eye, EyeOff, ChevronDown, ChevronRight, Lock } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { AddEditWebUrlProps, Credentials, WebUrlData } from "./interface";

export default function AddEditWebUrl({
    open,
    onOpenChange,
    mode,
    initialData,
    onSave,
    onEdit,
    webUrl
}: AddEditWebUrlProps) {
    const { toast } = useToast();
    const [url, setUrl] = useState("");
    const [description, setDescription] = useState("");
    const [isUrlValidated, setIsUrlValidated] = useState(false);
    const [validationError, setValidationError] = useState("");
    const [credentials, setCredentials] = useState<Credentials[]>([]);
    const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
    const [expandedCredentials, setExpandedCredentials] = useState<Record<string, boolean>>({});

    // ✅ Load data when editing
    useEffect(() => {
        if (open) {
            if (mode === "add") {
                // Reset form for add mode
                setUrl("");
                setDescription("");
                setIsUrlValidated(false);
                setValidationError("");
                setCredentials([]);
                setShowPasswords({});
                setExpandedCredentials({});
            }
            else if (mode === "edit" && webUrl) {
                // ✅ Prefill form for edit mode
                setUrl(webUrl.url || "");
                setDescription("");
                setIsUrlValidated(true);
                setValidationError("");
                // Map existing auth records into credentials
                const mappedCredentials =
                    webUrl.auth?.map((a: any) => ({
                        id: a.id?.toString() || Date.now().toString(),
                        email: a.email || "",
                        password: "", // password cannot be fetched, leave empty
                    })) || [];
                setCredentials(mappedCredentials);
                const expanded: Record<string, boolean> = {};
                mappedCredentials.forEach((c) => {
                    expanded[c.id] = false;
                });
                setExpandedCredentials(expanded);
            }
        }
    }, [open, mode, webUrl]);


    const validateUrlPattern = (inputUrl: string) => {
        const pattern = new RegExp(
            "^(https?:\\/\\/)" +
            "((www\\.)?" +
            "(([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.)+[a-zA-Z]{2,}|" +
            "(\\d{1,3}\\.){3}\\d{1,3}|" +
            "\\[?[a-fA-F\\d:]+\\])" +
            "(\\:\\d+)?" +
            "(\\/[-a-zA-Z\\d%@_.~+&:]*)*" +
            "(\\?[;&a-zA-Z\\d%@_.,~+&:=-]*)?" +
            "(\\#[-a-zA-Z\\d_]*)?$",
            "i",
        );

        return pattern.test(inputUrl);
    };


    // ✅ Everything else remains same below
    const validateUrl = () => {
        if (!url.trim()) {
            setValidationError("URL is required");
            setIsUrlValidated(false);
            return;
        }
        let formattedURL = url.trim();

        if (/^www\./i.test(url.trim())) {
            formattedURL = "https://" + url.trim();
        } else if (!/^https?:\/\//i.test(url.trim())) {
            formattedURL = "https://" + url.trim();
        }

        const urlPattern =
            /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;
        const isValid = urlPattern.test(formattedURL);


        if (isValid) {
            setUrl(formattedURL)
            setValidationError("");
            setIsUrlValidated(true);
            toast({
                title: "Validation Success",
                description: "URL is valid and ready to use",
            });
        } else {
            setValidationError("Invalid URL format. Please enter a valid web URL");
            setIsUrlValidated(false);
            toast({
                title: "Validation Failed",
                description: "Please enter a valid URL",
                variant: "destructive",
            });
        }
    };

    const addCredential = () => {
        const newCredential: Credentials = {
            id: Date.now().toString(),
            email: "",
            password: "",
        };
        setCredentials([...credentials, newCredential]);
        setExpandedCredentials((prev) => ({ ...prev, [newCredential.id]: true }));
    };

    const removeCredential = (id: string) => {
        setCredentials(credentials.filter((c) => c.id !== id));
    };

    const updateCredential = (
        id: string,
        field: "email" | "password",
        value: string
    ) => {
        setCredentials(
            credentials.map((c) => (c.id === id ? { ...c, [field]: value } : c))
        );
    };

    const toggleCredentialExpand = (id: string) => {
        setExpandedCredentials((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const togglePasswordVisibility = (id: string) => {
        setShowPasswords((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const saveCredential = (id: string) => {
        const credential = credentials.find((c) => c.id === id);
        if (!credential) return;
        if (!credential.email.trim()) {
            toast({
                title: "Validation Error",
                description: "Username is required",
                variant: "destructive",
            });
            return;
        }
        if (!credential.password.trim()) {
            toast({
                title: "Validation Error",
                description: "Password is required",
                variant: "destructive",
            });
            return;
        }
        const duplicateUsername = credentials.find(
            (c) =>
                c.id !== id &&
                c.email.trim().toLowerCase() === credential.email.trim().toLowerCase()
        );
        if (duplicateUsername) {
            toast({
                title: "Validation Error",
                description: "This username already exists",
                variant: "destructive",
            });
            return;
        }
        setExpandedCredentials((prev) => ({ ...prev, [id]: false }));
        toast({
            title: "Credential Saved",
            description: "Credential has been validated and saved",
        });
    };

    const canSave = () => {
        if (!url.trim() || !isUrlValidated) return false;
        if (credentials.length === 0) return false;
        return credentials.every((c) => c.email.trim());
    };

    const handleSave = () => {
        if (!url.trim()) {
            toast({
                title: "Error",
                description: "URL is required",
                variant: "destructive",
            });
            return;
        }

        if (!isUrlValidated) {
            toast({
                title: "Error",
                description: "Please validate the URL before saving",
                variant: "destructive",
            });
            return;
        }

        if (mode === "add") {
            // ✅ ADD MODE
            const data: WebUrlData = {
                url: url.trim(),
                description: description.trim(),
                credentials,
            };
            console.log("Add payload:", data);
            onSave(data);
        }
        else if (mode === "edit" && webUrl) {
            // ✅ EDIT MODE
            const existingAuth = webUrl.auth || [];

            const newauth = credentials.filter(
                (c) => !existingAuth.some((a: any) => a.email === c.email)
            );

            const updatedauth = credentials.filter((c) =>
                existingAuth.some(
                    (a: any) =>
                        a.email === c.email && c.password && c.password.trim().length > 0
                )
            );

            const removedauth = existingAuth.filter(
                (a: any) => !credentials.some((c) => c.email === a.email)
            );

            const editPayload = {
                id: webUrl.id,
                url: url.trim(),
                newauth,
                updatedauth,
                removedauth,
            };

            console.log("Edit payload:", editPayload);
            onEdit?.(editPayload);
        }

        handleClose();
    };


    const handleClose = () => {
        setUrl("");
        setDescription("");
        setIsUrlValidated(false);
        setValidationError("");
        setCredentials([]);
        setShowPasswords({});
        onOpenChange(false);
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3 text-xl">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                            <Globe className="w-5 h-5 text-blue-600" />
                        </div>
                        {mode === "add" ? "Add New Web URL" : "Edit Web URL"}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-6 py-4">
                    {/* URL Input */}
                    <div className="space-y-3">
                        <Label htmlFor="url" className="text-base font-semibold">
                            Web URL *
                        </Label>
                        <div className="flex gap-2">
                            <Input
                                id="url"
                                placeholder="Enter URL (e.g., app.example.com, https://portal.company.com)"
                                value={url}
                                onChange={(e) => {
                                    setUrl(e.target.value);
                                    setIsUrlValidated(false);
                                    setValidationError("");
                                }}
                                className="flex-1 h-10 text-base"
                                data-testid="input-url"
                            />
                            <Button
                                onClick={validateUrl}
                                disabled={!url.trim()}
                                className="gap-2 px-4"
                                data-testid="button-validate"
                            >
                                {isUrlValidated ? (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        Valid
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        Validate
                                    </>
                                )}
                            </Button>
                        </div>
                        {validationError && (
                            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                                <XCircle className="w-4 h-4" />
                                {validationError}
                            </div>
                        )}
                        {isUrlValidated && !validationError && (
                            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                                <CheckCircle className="w-4 h-4" />
                                URL validated successfully
                            </div>
                        )}
                    </div>
                    {/* Description */}
                    <div className="space-y-3">
                        <Label htmlFor="description" className="text-base font-semibold">
                            Description
                        </Label>
                        <Textarea
                            id="description"
                            placeholder="Describe this web application's purpose..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="text-base"
                            data-testid="input-description"
                        />
                    </div>
                    {/* Credentials Section */}
                    {isUrlValidated && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <Label className="text-base font-semibold">
                                    Credentials {credentials.length > 0 && `(${credentials.length})`}
                                </Label>
                                <Button
                                    size="sm"
                                    onClick={addCredential}
                                    className="gap-2"
                                    data-testid="button-add-credential"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Credential
                                </Button>
                            </div>
                            {credentials.length === 0 ? (
                                <div className="text-center py-8 border-2 border-dashed rounded-lg bg-muted/20">
                                    <p className="text-sm text-muted-foreground">
                                        No credentials added yet. Click "Add Credential" to add username/password combinations.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {credentials.map((credential, index) => (
                                        <Collapsible
                                            key={credential.id}
                                            open={expandedCredentials[credential.id]}
                                            onOpenChange={() => toggleCredentialExpand(credential.id)}
                                        >
                                            <div
                                                className="border rounded-lg bg-muted/10"
                                                data-testid={`credential-${credential.id}`}
                                            >
                                                <CollapsibleTrigger asChild>
                                                    <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/20">
                                                        <div className="flex items-center gap-2">
                                                            {expandedCredentials[credential.id] ? (
                                                                <ChevronDown className="w-4 h-4" />
                                                            ) : (
                                                                <ChevronRight className="w-4 h-4" />
                                                            )}
                                                            <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                                                            <span className="text-sm font-semibold">
                                                                Credential {index + 1}
                                                                {credential.email && credential.password && (
                                                                    <span className="ml-2 text-xs text-muted-foreground">
                                                                        ({credential.email})
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </div>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeCredential(credential.id);
                                                            }}
                                                            className="gap-1.5 text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 h-7 px-2"
                                                            data-testid={`button-remove-credential-${credential.id}`}
                                                        >
                                                            <Trash2 className="w-3 h-3" />
                                                            Remove
                                                        </Button>
                                                    </div>
                                                </CollapsibleTrigger>
                                                <CollapsibleContent>
                                                    <div className="px-4 pb-4 pt-2 space-y-3">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                            <div className="space-y-2">
                                                                <Label htmlFor={`username-${credential.id}`} className="text-sm">
                                                                    Username
                                                                </Label>
                                                                <Input
                                                                    id={`username-${credential.id}`}
                                                                    placeholder="Enter username"
                                                                    value={credential.email}
                                                                    onChange={(e) =>
                                                                        updateCredential(credential.id, "email", e.target.value)
                                                                    }
                                                                    className="h-9"
                                                                    data-testid={`input-username-${credential.id}`}
                                                                />
                                                            </div>
                                                            <div className="space-y-2">
                                                                <Label htmlFor={`password-${credential.id}`} className="text-sm">
                                                                    Password
                                                                </Label>
                                                                <div className="relative flex items-center">
                                                                    <Input
                                                                        id={`password-${credential.id}`}
                                                                        type={showPasswords[credential.id] ? "text" : "password"}
                                                                        placeholder="Enter password"
                                                                        value={credential.password}
                                                                        onChange={(e) =>
                                                                            updateCredential(credential.id, "password", e.target.value)
                                                                        }
                                                                        className="h-9 pr-10"
                                                                        data-testid={`input-password-${credential.id}`}
                                                                    />
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="absolute right-0 top-0 h-9 w-9"
                                                                        onClick={() => togglePasswordVisibility(credential.id)}
                                                                        data-testid={`button-toggle-password-${credential.id}`}
                                                                    >
                                                                        {showPasswords[credential.id] ? (
                                                                            <EyeOff className="w-4 h-4" />
                                                                        ) : (
                                                                            <Eye className="w-4 h-4" />
                                                                        )}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex justify-end pt-1">
                                                            <Button
                                                                size="sm"
                                                                onClick={() => saveCredential(credential.id)}
                                                                disabled={!credential.email.trim() || !credential.password.trim()}
                                                                className="gap-1.5"
                                                                data-testid={`button-save-credential-${credential.id}`}
                                                            >
                                                                <CheckCircle className="w-3 h-3" />
                                                                Save Credential
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </CollapsibleContent>
                                            </div>
                                        </Collapsible>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <DialogFooter className="gap-3">
                    <Button
                        variant="outline"
                        size="lg"
                        onClick={handleClose}
                        data-testid="button-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        size="lg"
                        onClick={handleSave}
                        disabled={!canSave()}
                        data-testid="button-save"
                    >
                        {mode === "add" ? "Add URL" : "Save Changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
